/**
 * Individual cancel journey on web: one dialog that walks
 * E (reason) -> F3 help / F3b career / F1 pause ->
 * G confirm -> H provider handoff (+ pending) -> I renewal is off.
 *
 * Rules (plan §3/§6):
 * - Nothing here changes billing. Every branch ends at the provider (H), and
 *   only a synced snapshot with `willRenew === false` shows "Renewal is off."
 * - Escape, the close button and the scrim close the journey; they never
 *   mutate billing. "<" steps back; reason, chip and drafts survive it
 *   (they live in the module journey store).
 * - Optional feedback is fire-and-forget and never blocks Continue.
 * - Free text never reaches analytics.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { normalizeLegacyPlanTier } from '@tabbio/utils/subscription/catalog';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import { useRevenueCat } from '../../hooks/useRevenueCat';
import { useCancelJourney } from '../../hooks/useCancelJourney';
import { useRetentionOptions } from '../../hooks/useRetentionOptions';
import { useSubscriptionState } from '../../hooks/useSubscriptionState';
import { useSubscriptionDisplay } from '../../lib/subscription-display';
import { trackProductEvent } from '../../lib/product-analytics';
import {
  CANCEL_ANALYTICS_EVENTS,
  CANCEL_REASON_LIST,
  PROVIDER_INTENT_STEP_KEYS,
  TABBIO_ATTRIBUTIONS,
  buildCancellationFeedback,
  cancelJourneyStore,
  detailLabelKey,
  formatCancelAccessDate,
  getCancelAccessDate,
  getCancelAnalyticsProperties,
  getCancellationScope,
  getConfirmMood,
  getHelpContent,
  isSuccessExit,
  type CancelJourneyStep,
  type ProviderIntent,
} from '../../lib/subscription-journey/cancel-journey';
import { submitCancellationFeedback } from '../../lib/subscription-journey/cv-feedback';
import { getTierPrices } from '../../lib/subscription-journey/pricing';
import { toJourneyLocale } from '../../lib/subscription-journey/store-links';
import {
  clearProviderHandoff,
  writeProviderHandoff,
} from '../../lib/subscription-handoff.web';
import { MaterialSymbol } from '../icons/MaterialSymbol';
import { NovaMood } from '../subscription/NovaMood.web';
import { ComparePlansDialog } from '../subscription/ComparePlansDialog.web';
import { IMPROVE_CV_HREF } from '../subscription/WelcomeContent.web';
import {
  Icon,
  JourneyDialog,
  SectionTitle,
  SubButton,
  SubGroup,
  SubHeading,
  SubInfo,
  SubList,
  SubRow,
  storeProviderKey,
} from '../subscription/journey-ui.web';

export type CancelJourneyEntry =
  | { step: 'reason' }
  | { step: 'provider'; intent: ProviderIntent; resume?: boolean }
  | { step: 'renewal-off' };

type Props = {
  open: boolean;
  onClose: () => void;
  /** Where the journey opens. Default: E (reason). */
  entry?: CancelJourneyEntry;
};

const REASON_ICONS: Record<(typeof CANCEL_REASON_LIST)[number]['icon'], string> = {
  briefcase: 'work',
  pause: 'pause',
  wallet: 'account_balance_wallet',
  thumbDown: 'thumb_down',
  help: 'help',
  bug: 'bug_report',
  ellipsis: 'more_horiz',
};

const TIER_NAMES = { plus: 'Plus', pro: 'Pro' } as const;

const STEP_TITLE_KEYS: Record<CancelJourneyStep, string> = {
  reason: 'subscription.cancel.reasons.title',
  help: 'subscription.cancel.help.title',
  career: 'subscription.cancel.career.title',
  pause: 'subscription.cancel.pause.title',
  confirm: 'subscription.cancel.confirm.title',
  provider: 'subscription.cancel.provider.title',
  'renewal-off': 'subscription.renewalOff.title',
};

function addMonths(iso: string, months: number) {
  const date = new Date(iso);
  date.setUTCMonth(date.getUTCMonth() + months);
  return date;
}

export function CancelPlanDialog(props: Props) {
  return props.open ? <CancelJourneySession {...props} /> : null;
}

function CancelJourneySession({ onClose, entry = { step: 'reason' } }: Props) {
  const { t, i18n } = useTranslation('common');
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const router = useRouter();
  const toast = useToast();
  const { user } = useAuth();
  const { snapshot, catalog } = useSubscriptionState();
  const display = useSubscriptionDisplay('web');
  const { openManagementUrl, syncSubscription } = useRevenueCat();
  const journey = useCancelJourney();
  const { state } = journey;
  const reason = state.reason;
  const retention = useRetentionOptions({ planSet: 'individual', reason });
  const [stack, setStack] = useState<CancelJourneyStep[]>([entry.step]);
  const [providerPending, setProviderPending] = useState(
    entry.step === 'provider' && Boolean(entry.resume),
  );
  const [checking, setChecking] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [pauseMonths, setPauseMonths] = useState<1 | 2 | 3>(1);
  const [opening, setOpening] = useState(false);
  const step = stack[stack.length - 1];

  const snapshotTier = normalizeLegacyPlanTier(snapshot?.tier ?? 'free');
  const tier: 'plus' | 'pro' = snapshotTier === 'pro' ? 'pro' : 'plus';
  const tierName = TIER_NAMES[tier];
  const provider = t(storeProviderKey(snapshot?.store));
  const accessDate = formatCancelAccessDate(getCancelAccessDate(snapshot), locale);
  const analytics = useMemo(
    () => getCancelAnalyticsProperties({ surface: 'web', snapshot }),
    [snapshot],
  );
  const analyticsRef = useRef(analytics);
  analyticsRef.current = analytics;

  // E starts (or resumes) the journey for this billing scope.
  useEffect(() => {
    if (entry.step === 'reason') {
      journey.start(getCancellationScope(snapshot, null), {
        tier: snapshotTier === 'free' ? 'free' : tier,
        accountCycle: snapshot?.billingInterval ?? null,
      });
      trackProductEvent(CANCEL_ANALYTICS_EVENTS.flowOpened, analyticsRef.current);
    } else if (entry.step === 'provider') {
      journey.setProviderIntent(entry.intent);
    }
    // Once per session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    journey.setContext({
      capabilities: retention.options.capabilities,
    });
    // `journey` is a fresh object per render; the store call is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retention.options]);

  const close = useCallback(
    (via: 'close' | 'keep' = 'close') => {
      trackProductEvent(CANCEL_ANALYTICS_EVENTS.flowClosed, {
        ...analyticsRef.current,
        step,
        via,
      });
      clearProviderHandoff();
      cancelJourneyStore.clear();
      onClose();
    },
    [onClose, step],
  );

  const push = (next: CancelJourneyStep) => {
    setStack((current) => [...current, next]);
    if (next === 'confirm') {
      trackProductEvent(CANCEL_ANALYTICS_EVENTS.lossScreenViewed, {
        ...analyticsRef.current,
        reason: cancelJourneyStore.getState().reason ?? 'skipped',
      });
    }
    if (next === 'help' || next === 'career') {
      trackProductEvent(CANCEL_ANALYTICS_EVENTS.retentionHelpOpened, {
        ...analyticsRef.current,
        reason: cancelJourneyStore.getState().reason ?? 'skipped',
      });
    }
  };
  const back =
    stack.length > 1 && !checking && !opening
      ? () => {
          setProviderPending(false);
          setStack((current) => current.slice(0, -1));
        }
      : undefined;

  const goToProvider = (intent: ProviderIntent) => {
    journey.setProviderIntent(intent);
    setProviderPending(false);
    push('provider');
  };

  /* ---------------------------- feedback ------------------------------ */
  const sendFeedback = () => {
    const current = cancelJourneyStore.getState();
    const payload = buildCancellationFeedback({
      reason: current.reason,
      detail: current.detail,
      draft: cancelJourneyStore.getDraft(),
      locale: toJourneyLocale(locale),
      planSet: 'individual',
      tier: snapshotTier === 'free' ? 'free' : tier,
      platform: 'web',
    });
    if (!payload) return;
    const idempotencyKey = cancelJourneyStore.requestKeyFor(payload);
    cancelJourneyStore.setFeedbackStatus('pending');
    void submitCancellationFeedback({ ...payload, idempotencyKey })
      .then(() => cancelJourneyStore.setFeedbackStatus('received'))
      .catch(() => {
        cancelJourneyStore.setFeedbackStatus('error');
        toast.error(t('subscription.cancel.help.feedbackFailed'));
      });
  };

  /* ---------------------------- provider ------------------------------ */
  const intent = state.providerIntent;
  const openProvider = async () => {
    if (opening) return;
    setOpening(true);
    const event = { ...analyticsRef.current, intent };
    trackProductEvent(CANCEL_ANALYTICS_EVENTS.providerManagementOpened, event);
    if (intent === 'cancel') {
      trackProductEvent(CANCEL_ANALYTICS_EVENTS.storeHandoff, event);
    }
    if (user?.id) writeProviderHandoff({ accountId: user.id, intent, tier });
    try {
      if (snapshot?.managementUrl) {
        await openManagementUrl(null);
      } else if (display.managementFallbackUrl) {
        window.location.assign(display.managementFallbackUrl);
      } else {
        throw new Error(display.managementDescription || t('settings.billingUnavailable'));
      }
      trackProductEvent(CANCEL_ANALYTICS_EVENTS.storeOpened, event);
      // If the destination opened without unloading this page, the next view
      // is the honest "Let's check your plan." state.
      setProviderPending(true);
    } catch (error) {
      clearProviderHandoff();
      trackProductEvent(CANCEL_ANALYTICS_EVENTS.storeOpenFailed, event);
      toast.error(
        t('settings.billingUnavailable'),
        error instanceof Error ? error.message : undefined,
      );
    } finally {
      setOpening(false);
    }
  };

  const checkProvider = useCallback(async () => {
    setChecking(true);
    try {
      const result = await syncSubscription({ planSet: 'individual', companyId: null });
      const next = result?.snapshot ?? null;
      const currentIntent = cancelJourneyStore.getState().providerIntent;
      if (currentIntent === 'renew' && next?.willRenew === true) {
        clearProviderHandoff();
        toast.success(t('settings.subscriptionSynced'));
        cancelJourneyStore.clear();
        onClose();
        return;
      }
      // A Free snapshot also carries `willRenew: false`; only a still-paid
      // plan whose renewal was switched off is "Renewal is off".
      if (currentIntent === 'cancel' && next?.willRenew === false && next.tier !== 'free') {
        clearProviderHandoff();
        setProviderPending(false);
        setStack((current) => [...current, 'renewal-off']);
        return;
      }
      // No confirmed change yet: current access stays as it is.
      setProviderPending(true);
    } catch {
      setProviderPending(true);
      toast.error(t('subscription.states.error.title'), t('subscription.states.error.body'));
    } finally {
      setChecking(false);
    }
  }, [onClose, syncSubscription, t, toast]);

  // Returning from the provider (handoff marker): check before saying anything.
  useEffect(() => {
    if (entry.step === 'provider' && entry.resume) void checkProvider();
    // Once per session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ------------------------------ steps ------------------------------- */
  let body: React.ReactNode = null;
  let footer: React.ReactNode = null;
  let title = t(STEP_TITLE_KEYS[step]);

  const keepPlan = (kind: 'primary' | 'secondary' | 'text' = 'text') => (
    <SubButton kind={kind} onClick={() => close('keep')}>
      {t('subscription.actions.keepMyPlan')}
    </SubButton>
  );

  if (step === 'reason') {
    body = (
      <>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <SubHeading title={t('subscription.cancel.reasons.heading')} />
          </div>
          <NovaMood mood="calm" size={96} />
        </div>
        <div
          role="radiogroup"
          aria-label={t('subscription.cancel.reasons.groupLabel')}
          style={{ marginTop: 14 }}
        >
          {CANCEL_REASON_LIST.map((item) => {
            const selected = reason === item.reason;
            return (
              <button
                key={item.reason}
                type="button"
                role="radio"
                aria-checked={selected}
                className="tabbio-sub-option"
                onClick={() => {
                  const next = journey.selectReason(item.reason);
                  trackProductEvent(CANCEL_ANALYTICS_EVENTS.reasonSelected, {
                    ...analyticsRef.current,
                    reason: next ?? 'cleared',
                  });
                }}
              >
                <span className="tabbio-sub-option-icon">
                  <MaterialSymbol name={REASON_ICONS[item.icon]} size={24} />
                </span>
                <span className="tabbio-sub-option-label">{t(item.labelKey)}</span>
                <span className={`tabbio-sub-circle${selected ? ' is-on' : ''}`} aria-hidden>
                  {selected ? <MaterialSymbol name="check" /> : null}
                </span>
              </button>
            );
          })}
        </div>
      </>
    );
    footer = (
      <>
        <SubButton
          onClick={() => {
            trackProductEvent(CANCEL_ANALYTICS_EVENTS.reasonSubmitted, {
              ...analyticsRef.current,
              reason: reason ?? 'skipped',
              skipped: !reason,
            });
            push(journey.nextAfterReason());
          }}
        >
          {t('subscription.actions.continue')}
        </SubButton>
        {keepPlan()}
      </>
    );
  } else if (step === 'help' && reason) {
    const content = getHelpContent(reason, state.detail);
    const draft = journey.draft;
    const chips = (
      <div className="tabbio-sub-chips" role="group" aria-label={t(content.kind === 'career' ? 'subscription.cancel.help.career.groupLabel' : content.titleKey)}>
        {content.options.map((option) => (
          <button
            key={option}
            type="button"
            className={`tabbio-sub-chip${content.kind === 'career' && TABBIO_ATTRIBUTIONS.has(option) ? ' is-tabbio' : ''}`}
            aria-pressed={state.detail === option}
            onClick={() => {
              const next = journey.selectDetail(option);
              trackProductEvent(
                content.kind === 'career'
                  ? CANCEL_ANALYTICS_EVENTS.careerAttributionSelected
                  : CANCEL_ANALYTICS_EVENTS.detailSelected,
                { ...analyticsRef.current, reason, detail: next ?? 'cleared' },
              );
            }}
          >
            {t(detailLabelKey(reason, option))}
          </button>
        ))}
      </div>
    );
    const note = content.promptKey ? (
      <>
        <label className="tabbio-sub-label" htmlFor="cancel-feedback-note">
          {t(content.promptKey)}
        </label>
        <textarea
          id="cancel-feedback-note"
          className="tabbio-sub-textarea"
          dir="auto"
          rows={3}
          maxLength={1000}
          value={draft}
          placeholder={content.placeholderKey ? t(content.placeholderKey) : undefined}
          aria-describedby={content.noteKey ? 'cancel-feedback-hint' : undefined}
          onChange={(event) => journey.setDraft(event.target.value)}
        />
        {content.noteKey ? (
          <p className="tabbio-sub-fine" id="cancel-feedback-hint">
            {t(content.noteKey)}
          </p>
        ) : null}
      </>
    ) : null;
    body = (
      <>
        <NovaMood mood={content.mood} size={88} />
        <div style={{ marginTop: 12 }}>
          <SubHeading title={t(content.titleKey)} />
        </div>
        {content.kind === 'career' ? (
          <>
            <SectionTitle>{t('subscription.cancel.help.career.heading')}</SectionTitle>
            {chips}
            {note}
          </>
        ) : content.kind === 'price' ? (
          <>
            <SubInfo>{t('subscription.cancel.help.priceInfo')}</SubInfo>
            <div style={{ marginTop: 14 }}>
              <SubGroup>
                <SubRow
                  icon="credit_card"
                  title={t('subscription.cancel.help.comparePlans')}
                  onClick={() => setCompareOpen(true)}
                />
              </SubGroup>
            </div>
          </>
        ) : (
          <>
            {chips}
            {note}
          </>
        )}
      </>
    );
    footer = (
      <>
        <SubButton
          onClick={() => {
            sendFeedback();
            push(journey.nextAfterHelp());
          }}
        >
          {t('subscription.actions.continue')}
        </SubButton>
        {content.showKeepPlan ? keepPlan() : null}
      </>
    );
  } else if (step === 'career') {
    body = (
      <>
        <NovaMood mood="happy" size={88} />
        <div style={{ marginTop: 12 }}>
          <SubHeading
            title={t('subscription.cancel.career.heading')}
            subtitle={t('subscription.cancel.career.subtitle')}
          />
        </div>
        <div style={{ marginTop: 20 }}>
          <SubList
            items={[
              t('subscription.cancel.career.features.current'),
              t('subscription.cancel.career.features.identity'),
              t('subscription.cancel.career.features.opportunities'),
            ]}
          />
        </div>
        <p className="tabbio-sub-fine">{t('subscription.cancel.career.fine')}</p>
      </>
    );
    footer = (
      <>
        {keepPlan('primary')}
        <SubButton kind="text" onClick={() => push(journey.nextAfterCareer())}>
          {t('subscription.actions.continueCancellation')}
        </SubButton>
      </>
    );
  } else if (step === 'pause') {
    const available = state.context.capabilities.pause;
    if (!available) {
      body = (
        <>
          <NovaMood mood="sleeping" size={88} />
          <div style={{ marginTop: 12 }}>
            <SubHeading
              title={t('subscription.cancel.pause.unavailable.heading')}
              subtitle={
                accessDate
                  ? t('subscription.cancel.pause.unavailable.body', { provider, date: accessDate })
                  : t('subscription.cancel.pause.unavailable.bodyNoDate', { provider })
              }
            />
          </div>
          <SubInfo>{t('subscription.cancel.pause.unavailable.info')}</SubInfo>
        </>
      );
      footer = (
        <>
          <SubButton kind="secondary" onClick={() => push(journey.nextAfterPauseDeclined())}>
            {t('subscription.cancel.pause.noThanks')}
          </SubButton>
          {keepPlan()}
        </>
      );
    } else {
      const startIso = getCancelAccessDate(snapshot);
      const resume = startIso ? addMonths(startIso, pauseMonths) : null;
      const reminder = resume ? new Date(resume.getTime() - 3 * 24 * 60 * 60 * 1000) : null;
      const format = (value: Date | string | null) =>
        value ? formatCancelAccessDate(typeof value === 'string' ? value : value.toISOString(), locale) : null;
      const monthly = getTierPrices(catalog, 'individual', tier)?.monthlyDisplay ?? '';
      const rows: [string, string, string | null, string][] = [
        ['pause', t('subscription.cancel.pause.starts'), format(startIso), t('subscription.cancel.pause.startsBody')],
        ['notifications', t('subscription.cancel.pause.reminder'), format(reminder), t('subscription.cancel.pause.reminderBody')],
        ['credit_card', t('subscription.cancel.pause.resumes'), format(resume), t('subscription.cancel.pause.resumesBody', { price: monthly })],
      ];
      body = (
        <>
          <NovaMood mood="sleeping" size={88} />
          <div style={{ marginTop: 12 }}>
            <SubHeading
              title={t('subscription.cancel.pause.heading')}
              subtitle={t('subscription.cancel.pause.subtitle')}
            />
          </div>
          <div
            className="tabbio-sub-seg is-plain"
            role="radiogroup"
            aria-label={t('subscription.cancel.pause.lengthLabel')}
            style={{ marginTop: 20, gridTemplateColumns: 'repeat(3, 1fr)' }}
          >
            {([1, 2, 3] as const).map((months) => (
              <button
                key={months}
                type="button"
                role="radio"
                aria-checked={pauseMonths === months}
                onClick={() => setPauseMonths(months)}
              >
                {t(`subscription.cancel.pause.months.${months === 1 ? 'one' : months === 2 ? 'two' : 'three'}`)}
              </button>
            ))}
          </div>
          <div className="tabbio-sub-timeline">
            {rows.map(([icon, rowTitle, date, sub]) => (
              <div key={icon}>
                <span className="tabbio-sub-row-icon">
                  <MaterialSymbol name={icon} size={22} />
                </span>
                <div>
                  <strong>
                    {date
                      ? t('subscription.cancel.pause.timelineRow', { date, title: rowTitle })
                      : rowTitle}
                  </strong>
                  <p>{sub}</p>
                </div>
              </div>
            ))}
          </div>
        </>
      );
      footer = (
        <>
          <SubButton onClick={() => goToProvider('pause')}>
            {t('subscription.cancel.pause.review')}
          </SubButton>
          <SubButton kind="secondary" onClick={() => push(journey.nextAfterPauseDeclined())}>
            {t('subscription.cancel.pause.noThanks')}
          </SubButton>
        </>
      );
    }
  } else if (step === 'confirm') {
    const success = isSuccessExit(reason);
    body = (
      <>
        <NovaMood mood={getConfirmMood(reason)} size={96} />
        <div style={{ marginTop: 14 }}>
          <SubHeading
            title={t(
              success
                ? 'subscription.cancel.confirm.successHeading'
                : 'subscription.cancel.confirm.heading',
            )}
            subtitle={t(
              success
                ? 'subscription.cancel.confirm.successSubtitle'
                : 'subscription.cancel.confirm.subtitle',
            )}
          />
        </div>
        <div className="tabbio-sub-date-hero">
          {accessDate ? (
            <>
              <span>{t('subscription.cancel.confirm.benefitsStayUntil', { tier: tierName })}</span>
              <strong>{accessDate}</strong>
            </>
          ) : (
            <strong style={{ fontSize: 20 }}>{t('subscription.cancel.confirm.accessUnknown')}</strong>
          )}
        </div>
        <SectionTitle>{t('subscription.cancel.confirm.staysTitle')}</SectionTitle>
        <SubList
          variant="stays"
          items={[
            t('subscription.cancel.confirm.stays.savedCv'),
            t('subscription.cancel.confirm.stays.publicProfile'),
          ]}
        />
        <SectionTitle>
          {accessDate
            ? t('subscription.cancel.confirm.stopsAfter', { date: accessDate })
            : t('subscription.cancel.confirm.stopsLater')}
        </SectionTitle>
        <SubList
          variant="stops"
          icon="close"
          items={[
            t('subscription.cancel.confirm.stops.paidAi'),
            t('subscription.cancel.confirm.stops.premium'),
          ]}
        />
        <p className="tabbio-sub-fine">{t('subscription.cancel.confirm.fine', { provider })}</p>
      </>
    );
    footer = (
      <>
        <SubButton onClick={() => goToProvider('cancel')}>
          {t('subscription.cancel.confirm.continueToCancel')}
        </SubButton>
        {keepPlan('secondary')}
      </>
    );
  } else if (step === 'provider') {
    const providerIcon =
      snapshot?.store === 'app_store'
        ? 'phone_iphone'
        : snapshot?.store === 'play_store'
          ? 'shop'
          : 'credit_card';
    const intentLabel = t(PROVIDER_INTENT_STEP_KEYS[intent]);
    body = (
      <>
        <div className="tabbio-sub-provider-mark">
          <MaterialSymbol name={providerIcon} size={34} />
        </div>
        <SubHeading
          title={
            providerPending
              ? t('subscription.cancel.provider.pendingHeading')
              : t('subscription.cancel.provider.heading', { provider })
          }
          subtitle={
            providerPending
              ? t('subscription.cancel.provider.pendingSubtitle')
              : t('subscription.cancel.provider.subtitle')
          }
        />
        <ol className="tabbio-sub-steps" style={{ listStyle: 'none', padding: 0 }}>
          {[
            t('subscription.cancel.provider.steps.open', { provider }),
            t('subscription.cancel.provider.steps.find'),
            intentLabel,
          ].map((text, index) => (
            <li key={index} style={{ display: 'flex', alignItems: 'center', gap: 14, fontSize: 15 }}>
              <span
                aria-hidden
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  background: 'var(--sub-group)',
                  display: 'grid',
                  placeItems: 'center',
                  fontSize: 14,
                  color: 'var(--text-secondary)',
                  flexShrink: 0,
                }}
              >
                {index + 1}
              </span>
              <span>{text}</span>
            </li>
          ))}
        </ol>
        {providerPending ? (
          <div role="status">
            <SubInfo icon="schedule" warning>
              {t('subscription.cancel.provider.pendingInfo')}
            </SubInfo>
          </div>
        ) : (
          <SubInfo>{t('subscription.cancel.provider.returnInfo')}</SubInfo>
        )}
      </>
    );
    footer = (
      <>
        {providerPending ? (
          <SubButton
            onClick={() => void checkProvider()}
            disabled={checking}
            aria-busy={checking}
            icon={<Icon name="refresh" />}
          >
            {t('subscription.cancel.provider.checkAgain')}
          </SubButton>
        ) : (
          <SubButton
            onClick={() => void openProvider()}
            disabled={opening}
            aria-busy={opening}
            icon={<Icon name="arrow_outward" flip />}
          >
            {t('subscription.cancel.provider.steps.open', { provider })}
          </SubButton>
        )}
        <SubButton kind="text" onClick={() => close('keep')} disabled={checking}>
          {t('subscription.actions.backToMyPlan')}
        </SubButton>
      </>
    );
  } else if (step === 'renewal-off') {
    body = (
      <>
        <div style={{ position: 'relative', width: 96 }}>
          <NovaMood mood="calm" size={88} />
          <span
            className="tabbio-sub-mark"
            style={{ position: 'absolute', insetInlineEnd: 0, bottom: 4 }}
          >
            <MaterialSymbol name="check_circle" size={26} />
          </span>
        </div>
        <div style={{ marginTop: 12 }}>
          <SubHeading
            title={t('subscription.renewalOff.heading')}
            subtitle={t('subscription.renewalOff.subtitle')}
          />
        </div>
        <div className="tabbio-sub-date-hero">
          {accessDate ? (
            <>
              <span>{t('subscription.renewalOff.benefitsEnd', { tier: tierName })}</span>
              <strong>{accessDate}</strong>
            </>
          ) : (
            <strong style={{ fontSize: 20 }}>{t('subscription.billing.accessUnknown')}</strong>
          )}
        </div>
        <SubInfo>{t('subscription.renewalOff.info')}</SubInfo>
      </>
    );
    footer = (
      <>
        <SubButton
          onClick={() => {
            close('keep');
            router.push(IMPROVE_CV_HREF as never);
          }}
        >
          {t('subscription.renewalOff.keepUsing')}
        </SubButton>
        <SubButton kind="secondary" onClick={() => goToProvider('renew')}>
          {t('subscription.renewalOff.turnRenewalOn')}
        </SubButton>
      </>
    );
  } else {
    // A step that needs a reason (help) without one: fall back to E.
    body = null;
    footer = (
      <SubButton onClick={() => setStack(['reason'])}>
        {t('subscription.actions.back')}
      </SubButton>
    );
  }

  return (
    <>
      <JourneyDialog
        title={title}
        onClose={() => close('close')}
        onBack={back}
        footer={footer}
        stepKey={`${step}:${stack.length}:${providerPending ? 'p' : 'r'}`}
      >
        {body}
      </JourneyDialog>
      {compareOpen ? <ComparePlansDialog onClose={() => setCompareOpen(false)} zIndex={1300} /> : null}
    </>
  );
}

